import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table'
import { ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Columns3, Filter } from 'lucide-react'
import type { Candidate } from '@/lib/types'
import { connectionLabel, matchesColumnValue, type GrowthTableState, type GrowthExportView } from '@/lib/growthView'

const columnHelper = createColumnHelper<Candidate>()

// Score bar visualization component
function ScoreBar({ score, tooltip }: { score: number; tooltip?: string }) {
  const color =
    score >= 70
      ? 'bg-green-500'
      : score >= 50
      ? 'bg-yellow-500'
      : 'bg-red-500'

  const textColor =
    score >= 70
      ? 'text-green-400'
      : score >= 50
      ? 'text-yellow-400'
      : 'text-red-400'

  return (
    <div className="group relative flex items-center gap-2 min-w-[78px]">
      <div className="flex-1 h-1 bg-slate-800 overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className={`font-mono text-xs font-medium ${textColor} w-7 text-right`}>
        {score}
      </span>
      {tooltip && (
        <div className="pointer-events-none absolute left-0 top-6 z-30 hidden w-80 rounded-md border border-slate-700 bg-slate-950 p-3 text-xs leading-relaxed text-slate-200 shadow-xl group-hover:block">
          {tooltip}
        </div>
      )}
    </div>
  )
}

function toPercentScore(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 0
  return value <= 1 ? Math.round(value * 100) : Math.round(value)
}

function reasoningText(row: Candidate) {
  if (Array.isArray(row.reasoning)) return row.reasoning.join(' ')
  return row.reasoning || row.explanation || ''
}

const columns: ColumnDef<Candidate, any>[] = [
  columnHelper.accessor('activity_status', {
    header: 'Activity',
    filterFn: (row, id, value) => value === 'unknown' ? !['active', 'inactive'].includes(row.getValue(id)) : row.getValue(id) === value,
    cell: info => { const status = info.getValue() || 'unchecked'; return <span title={info.row.original.activity_reason} className={`rounded px-2 py-1 text-xs ${status === 'active' ? 'bg-green-100 text-green-800' : status === 'inactive' ? 'bg-slate-200 text-slate-700' : 'bg-amber-50 text-amber-800'}`}>{status === 'error' ? 'Unknown (failed)' : status === 'unchecked' ? 'Unknown (unchecked)' : status}</span> },
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor('title', {
    header: 'Title',
    cell: (info) => (
      <span className="text-sm text-slate-400 max-w-[260px] truncate block">
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('company', { header: 'Company', cell: info => <span className="text-sm">{info.getValue() || '-'}</span> }),
  columnHelper.accessor('location', {
    header: 'Location',
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),
  columnHelper.accessor(row => connectionLabel(row.network_distance), {
    id: 'network_distance',
    header: 'Connection',
    cell: (info) => {
      const distance = info.getValue()
      if (distance == null) return <span className="text-xs text-slate-500">-</span>

      return (
        <span className="text-xs font-medium text-blue-400">
          {distance || '-'}
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.followers ?? row.followers_count, {
    id: 'followers',
    header: 'Followers',
    cell: (info) => {
      const count = info.getValue()
      return (
        <span className="font-mono text-sm">
          {count != null ? count.toLocaleString() : '-'}
        </span>
      )
    },
  }),
  columnHelper.accessor(row => row.intent_match == null ? null : toPercentScore(row.intent_match), {
    id: 'intent_match',
    header: 'Intent',
    cell: (info) => <ScoreBar score={info.getValue() ?? 0} tooltip={reasoningText(info.row.original)} />,
  }),
  columnHelper.accessor('post_relevance', {
    header: 'Post',
    cell: (info) => {
      const score = info.getValue()
      if (score == null) return <span className="text-xs text-slate-500">-</span>
      return <ScoreBar score={score} />
    },
  }),
  columnHelper.accessor('baseline_score', {
    header: 'Baseline',
    cell: (info) => <ScoreBar score={info.getValue()} />,
  }),
  columnHelper.accessor('final_score', {
    header: 'Final',
    cell: (info) => <ScoreBar score={info.getValue()} />,
  }),
  columnHelper.accessor(row => row.source === 'engage' ? 'Engager' : 'Sales Nav', {
    id: 'source',
    header: 'Source',
    cell: (info) => {
      const source = info.getValue()
      return (
        <span
          className={
            source === 'Engager'
              ? 'px-1.5 py-0.5 rounded text-xs bg-slate-800 text-slate-200'
              : 'px-1.5 py-0.5 rounded text-xs bg-slate-900 text-slate-300'
          }
        >
          {source}
        </span>
      )
    },
  }),
  columnHelper.accessor('url', {
    header: 'Profile',
    cell: (info) => {
      const url = info.getValue()
      if (!url) return null
      // Ensure URL has protocol prefix for proper external linking
      const href = url.startsWith('http') ? url : `https://${url}`
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      )
    },
  }),
  columnHelper.accessor('latest_post_date', { header: 'Latest Post Date' }),
  columnHelper.accessor('activity_checked_at', { header: 'Activity Checked At' }),
  columnHelper.accessor('activity_reason', { header: 'Activity Reason' }),
]

interface GrowthListTableProps {
  candidates: Candidate[]
  state: GrowthTableState
  setState: Dispatch<SetStateAction<GrowthTableState>>
  onViewChange: (view: GrowthExportView) => void
}

export function GrowthListTable({ candidates, state, setState, onViewChange }: GrowthListTableProps) {
  const [showFilters, setShowFilters] = useState(false)

  const table = useReactTable({
    data: candidates,
    columns,
    columnResizeMode: 'onChange',
    state,
    defaultColumn: { minSize: 70, maxSize: 800, filterFn: (row, id, value) => matchesColumnValue(row.getValue(id), String(value)) },
    getRowId: (row, index) => String(row._atlas_row_index ?? index),
    onSortingChange: update => setState(previous => ({ ...previous, sorting: typeof update === 'function' ? update(previous.sorting) : update })),
    onColumnFiltersChange: update => setState(previous => ({ ...previous, columnFilters: typeof update === 'function' ? update(previous.columnFilters) : update })),
    onColumnVisibilityChange: update => setState(previous => ({ ...previous, columnVisibility: typeof update === 'function' ? update(previous.columnVisibility) : update })),
    onColumnSizingChange: update => setState(previous => ({ ...previous, columnSizing: typeof update === 'function' ? update(previous.columnSizing) : update })),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })
  const viewKey = JSON.stringify({ row_indices: table.getRowModel().rows.map(row => row.original._atlas_row_index ?? Number(row.id)),
    columns: table.getVisibleLeafColumns().map(column => ({ id: column.id, width: column.getSize() })) })
  useEffect(() => { onViewChange(JSON.parse(viewKey)) }, [viewKey, state, onViewChange])
  const moveColumn = (id: string, direction: number) => {
    const order = table.getAllLeafColumns().map(column => column.id)
    const index = order.indexOf(id), target = index + direction
    if (target < 0 || target >= order.length) return
    ;[order[index], order[target]] = [order[target], order[index]]
    setState(previous => ({ ...previous, columnOrder: order }))
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <details className="relative">
          <summary className="flex cursor-pointer list-none items-center gap-1 rounded border border-slate-700 px-2 py-1.5"><Columns3 className="h-3.5 w-3.5" />Columns</summary>
          <div className="absolute left-0 top-full z-40 mt-1 max-h-96 w-72 overflow-auto rounded border border-slate-700 bg-slate-950 p-2 shadow-lg">
            <p className="mb-2 text-slate-400">Show, hide and reorder columns</p>
            {table.getAllLeafColumns().map((column, index, all) => <div key={column.id} className="flex items-center gap-2 py-1">
              <label className="flex flex-1 items-center gap-2"><input type="checkbox" checked={column.getIsVisible()} disabled={column.getIsVisible() && table.getVisibleLeafColumns().length === 1} onChange={column.getToggleVisibilityHandler()} />{String(column.columnDef.header)}</label>
              <button aria-label={`Move ${column.columnDef.header} left`} disabled={index === 0} onClick={() => moveColumn(column.id, -1)} className="disabled:opacity-25"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button aria-label={`Move ${column.columnDef.header} right`} disabled={index === all.length - 1} onClick={() => moveColumn(column.id, 1)} className="disabled:opacity-25"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>)}
          </div>
        </details>
        <button onClick={() => setShowFilters(!showFilters)} aria-pressed={showFilters} className="flex items-center gap-1 rounded border border-slate-700 px-2 py-1.5"><Filter className="h-3.5 w-3.5" />Filters{state.columnFilters.length ? ` (${state.columnFilters.length})` : ''}</button>
        {!!state.columnFilters.length && <button onClick={() => table.resetColumnFilters()} className="text-blue-400">Clear filters</button>}
        <span className="text-slate-500">{table.getRowModel().rows.length} of {candidates.length} profiles · XLSX matches this view</span>
      </div>
      <div className="max-w-full overflow-auto rounded-md border border-slate-800">
      <table className="table-fixed text-sm" style={{ width: table.getTotalSize(), minWidth: '100%' }}>
        <thead className="bg-slate-900 border-b border-slate-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="relative px-3 py-2 text-left text-xs font-medium text-slate-400"
                  style={{ width: header.getSize() }}
                >
                  {header.column.getCanSort() ? (
                    <button
                      onClick={header.column.getToggleSortingHandler()}
                      className="flex items-center gap-1 hover:text-slate-200 transition-colors"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                  <button
                    type="button"
                    aria-label="Resize column"
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-slate-700/0 hover:bg-slate-500"
                  />
                </th>
              ))}
            </tr>
          ))}
          {showFilters && <tr>{table.getVisibleLeafColumns().map(column => <th key={column.id} className="px-2 pb-2 text-left">
            {column.id === 'activity_status' ? <select aria-label="Filter Activity" value={String(column.getFilterValue() ?? '')} onChange={event => column.setFilterValue(event.target.value)} className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs font-normal text-slate-200">
              <option value="">All</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="unknown">Unknown (all)</option><option value="unchecked">Unchecked</option><option value="error">Failed</option>
            </select> : <input aria-label={`Filter ${column.columnDef.header}`} placeholder="Filter…" value={String(column.getFilterValue() ?? '')} onChange={event => column.setFilterValue(event.target.value)} className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs font-normal text-slate-200" />}
          </th>)}</tr>}
        </thead>
        <tbody className="divide-y divide-slate-800">
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="hover:bg-slate-900">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="overflow-hidden break-words px-3 py-2 align-top" style={{ width: cell.column.getSize() }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          {!table.getRowModel().rows.length && <tr><td colSpan={table.getVisibleLeafColumns().length} className="p-8 text-center text-slate-500">No profiles match these filters. Clear filters or select All activity.</td></tr>}
        </tbody>
      </table>
      </div>
      {showFilters && <p className="text-xs text-slate-500">Text filters are case-insensitive. Numeric filters also accept &gt;=70 or 50..90. Resize columns by dragging their header edge.</p>}
    </div>
  )
}
